import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import { Search } from "../../assets/icons/icons";
import { useTranslation } from "react-i18next";

const API_BASE = "https://laravel-api.aj-mart.com";

const buildStorageUrl = (path = "") =>
  `${API_BASE}/storage/app/public/${String(path || "").replace(/^\/+/, "")}`;

export default function SearchSection({ desktop = false }) {
  const { t, i18n } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await axios.get(`${API_BASE}/api/products`);

        const list =
          res?.data?.products ||
          res?.data?.data ||
          [];

        setProducts(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Search products error:", err);

        setProducts([]);
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return [];

    return products
      .filter((product) => {
        const arabicName = String(
          product?.product_name_A || ""
        ).toLowerCase();

        const englishName = String(
          product?.product_name_E || ""
        ).toLowerCase();

        const arabicDesc = String(
          product?.product_desc_A || ""
        ).toLowerCase();

        const englishDesc = String(
          product?.product_desc_E || ""
        ).toLowerCase();

        return (
          arabicName.includes(term) ||
          englishName.includes(term) ||
          arabicDesc.includes(term) ||
          englishDesc.includes(term)
        );
      })
      .slice(0, 8);
  }, [products, searchTerm]);

  const getProductName = (product) => {
    if (i18n.language === "ar") {
      return (
        product?.product_name_A ||
        product?.product_name_E ||
        "Product"
      );
    }

    return (
      product?.product_name_E ||
      product?.product_name_A ||
      "Product"
    );
  };

  const closeSearch = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  // =========================
  // DESKTOP
  // =========================
  if (desktop) {
    return (
      <div className="relative w-[450px]">
        <div className="absolute left-[12px] top-1/2 -translate-y-1/2 z-10">
          <Search />
        </div>

        <input
          type="search"
          placeholder={t("Search Product")}
          value={searchTerm}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          className="
            rounded-[7px]
            w-full
            py-[9px]
            px-[40px]
            bg-[#f5f5f5]
            border-0
            outline-0
            shadow-[0px_0px_10px_#00000024]
          "
        />

        {isOpen && searchTerm.trim() && (
          <div
            className="
              absolute
              top-[48px]
              left-0
              w-full
              bg-white
              rounded-[8px]
              shadow-xl
              border
              border-gray-100
              z-[9999]
              max-h-[420px]
              overflow-y-auto
            "
          >
            {loading && (
              <div className="py-4 text-center text-gray-500">
                Loading...
              </div>
            )}

            {!loading && error && (
              <div className="py-4 text-center text-red-500">
                {error}
              </div>
            )}

            {!loading &&
              !error &&
              filteredProducts.length === 0 && (
                <div className="py-4 text-center text-gray-500">
                  No products found
                </div>
              )}

            {!loading &&
              filteredProducts.map((product) => {
                const name = getProductName(product);

                const image = product?.product_main_img
                  ? buildStorageUrl(product.product_main_img)
                  : "https://placehold.co/80x80?text=No+Image";

                const price = Number(
                  product?.product_price || 0
                );

                const discount = Number(
                  product?.product_price_discount || 0
                );

                const finalPrice =
                  discount > 0
                    ? (
                        price *
                        (1 - discount / 100)
                      ).toFixed(2)
                    : price.toFixed(2);

                return (
                  <Link
                    key={product.product_id}
                    to={`/productdetails/${product.product_id}`}
                    onClick={closeSearch}
                    className="
                      flex
                      items-center
                      gap-3
                      px-3
                      py-3
                      border-b
                      border-gray-100
                      hover:bg-gray-50
                      transition
                    "
                  >
                    <div className="w-[55px] h-[55px] border rounded-md bg-white overflow-hidden flex-shrink-0">
                      <img
                        src={image}
                        alt={name}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[14px] text-gray-800 line-clamp-1">
                        {name}
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-bold text-[#1485a6] text-sm">
                          {finalPrice} {t("JOD")}
                        </span>

                        {discount > 0 && (
                          <span className="line-through text-gray-400 text-xs">
                            {price.toFixed(2)} {t("JOD")}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>
        )}
      </div>
    );
  }

  // =========================
  // MOBILE
  // =========================
  return (
    <>
      <div
        className="w-fit cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <Search />
      </div>

      {isOpen && (
        <div
          className="
            fixed
            inset-0
            bg-black/50
            flex
            justify-center
            items-start
            pt-[100px]
            z-[9999]
          "
          onClick={closeSearch}
        >
          <div
            className="
              bg-white
              rounded-[10px]
              p-[20px]
              w-[90%]
              max-w-[500px]
              shadow-lg
              max-h-[80vh]
              overflow-hidden
            "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-[15px]">
              <h3 className="text-lg font-bold">
                {t("Search")}
              </h3>

              <button
                type="button"
                onClick={closeSearch}
                className="text-gray-500 hover:text-gray-700"
              >
                ✖
              </button>
            </div>

            <input
              type="search"
              placeholder={t("Search Product")}
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
              autoFocus
              className="
                rounded-[7px]
                w-full
                py-[9px]
                px-[15px]
                bg-[#f5f5f5]
                border
                border-gray-300
                outline-0
                shadow-[0px_0px_10px_#00000024]
              "
            />

            <div className="mt-4 max-h-[450px] overflow-y-auto">
              {loading && (
                <div className="text-center py-5 text-gray-500">
                  Loading...
                </div>
              )}

              {!loading &&
                searchTerm.trim() &&
                filteredProducts.length === 0 && (
                  <div className="text-center py-5 text-gray-500">
                    No products found
                  </div>
                )}

              {!loading &&
                filteredProducts.map((product) => {
                  const name = getProductName(product);

                  const image = product?.product_main_img
                    ? buildStorageUrl(
                        product.product_main_img
                      )
                    : "https://placehold.co/80x80?text=No+Image";

                  const price = Number(
                    product?.product_price || 0
                  );

                  return (
                    <Link
                      key={product.product_id}
                      to={`/productdetails/${product.product_id}`}
                      onClick={closeSearch}
                      className="
                        flex
                        items-center
                        gap-3
                        p-3
                        border-b
                        border-gray-100
                        hover:bg-gray-50
                      "
                    >
                      <div className="w-[60px] h-[60px] border rounded bg-white overflow-hidden">
                        <img
                          src={image}
                          alt={name}
                          className="w-full h-full object-contain"
                        />
                      </div>

                      <div>
                        <div className="font-semibold text-sm">
                          {name}
                        </div>

                        <div className="text-[#1485a6] font-bold text-sm">
                          {price.toFixed(2)} {t("JOD")}
                        </div>
                      </div>
                    </Link>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}